import type { JSX } from 'preact';
import { CLINIC_TIME_ZONE, clinicNow } from '../lib/clinicDay';
import { useEffect, useState } from 'preact/hooks';
import { Nav } from './Nav';
import type { Role } from '../lib/nav';
import { session } from '../lib/session';
import { signOut } from '../lib/auth';
import { navigate } from '../lib/router';
import { formatDate } from '../lib/date';
import { viewAsLocation, setViewAs } from '../lib/viewAs';
import { LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { currentTheme, setTheme, type Theme } from '../lib/theme';

// The weekday names, spelled out. SPEC-001 §13 bans MONTH names in a rendered
// date; a weekday is not a month and is not part of the date string itself —
// the date beside it is still MM/DD/YYYY, produced by formatDate.
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The clock half: "2:05 PM". A 12-hour clock with no seconds shown and no
 * timezone name — R-15 permits the time, and still permits no zone. */
function clockOf(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIME_ZONE, hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(now);
  return parts.replace(/\u202f/g, ' ').toUpperCase();
}

/** "SATURDAY · 08/15/2026 · 2:05 PM" — owner item 7, top right of the masthead,
 * on the wordmark's own line and above the signed-in email.
 *
 * The TIME is here under owner ruling R-15 (2026-08-15), which amends §13's
 * "never a time". The DATE is still MM/DD/YYYY and no timezone is rendered. */
export function todayLine(now: Date): string {
  // THE CLINICS' CLOCK, not the reader's. The owner reads this app from seven
  // hours ahead of Florida; his masthead said Monday the 17th while the clinics
  // were still working Sunday evening, and every date-driven surface disagreed
  // with it. One clock, America/New_York, for everyone (lib/clinicDay.ts).
  const day = clinicNow(now);
  return `${WEEKDAYS[day.getDay()]} · ${formatDate(day)} · ${clockOf(now)}`.toUpperCase();
}

// The four clinics, in the fixed display order used everywhere else in the app
// (SPEC-001 §12 nav / decisions/LOG.md 2026-08-12). The names and the order now
// come from lib/locations.ts — the ONE source both the "where" line and the
// view-as pulldown read, so the pulldown's option order cannot drift from the
// canonical one (that drift is finding F-9).
const CLINICS = LOCATION_ORDER.map((code) => LOCATION_NAMES[code]);

export function Masthead({ role }: { role: Role }): JSX.Element {
  const s = session.value;
  const email = s.status === 'signed-in' ? s.me?.email : undefined;

  // The lens. `canViewAs` is the server's own answer (src/access.js: OWNER
  // only — a DIRECTOR sees all four clinics but may NOT pretend to be one),
  // so the control appears exactly where the server would honour it. A
  // manager or a director never sees it at all.
  const canViewAs = s.me?.canViewAs === true;
  const lens = viewAsLocation.value;
  const lensName = lens ? (LOCATION_NAMES[lens as LocationCode] ?? lens) : null;

  // A RUNNING clock (owner, 2026-08-15). Ticking every 15 seconds, not every
  // second: the minute is the smallest thing shown, so a per-second timer would
  // re-render the whole masthead 60 times a minute to change nothing. The
  // interval is cleared on unmount — a timer that outlives its component is how
  // a signed-out app keeps working in the background.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <div class="mast">
        <div class="lockup">
          <div class="wm">
            <span>nv</span>
            <b>Manager</b>
          </div>
          <div class="rule" />
          <div class="tag">MASSAGE ENVY GROUP</div>
          {/* Under a lens this line names the ONE clinic being viewed. Leaving
              all four listed while the rest of the app shows one is how the
              owner ends up unsure whose view is on screen — the exact
              confusion the lens exists to remove. */}
          <div class="where" data-testid="masthead-where">
            {(lensName ? [lensName] : CLINICS).map((name, i) => (
              <span key={name}>
                {i > 0 && <i> / </i>}
                {name}
              </span>
            ))}
          </div>
        </div>
        <div class="sp" />
        {/* OPERATIONS removed (owner, 2026-08-15) — it was the word he read as
            "Operation" on the task screen, and it labelled nothing. The day,
            date and clock take that spot, large and in capitals, on the
            wordmark's own line and above the signed-in account. */}
        <div class="mast-right">
          <div class="today" data-testid="masthead-today">
            {todayLine(now)}
            <ThemeSwitch />
          </div>
          {email && <div class="who">{email}</div>}
          {/* Sign out belongs to the ACCOUNT, so it sits with the account —
              under the email (owner, 2026-08-15), not out on the nav row among
              the pages. */}
          {email && (
            <button
              type="button"
              class="sign-out-button"
              onClick={() => {
                void signOut();
                // Drop back to the signed-out state (Shell/session.ts) so the
                // app does not keep rendering App/Nav for a cleared session.
                session.value = { status: 'signed-out', me: null, error: null };
                navigate('/');
              }}
            >
              Sign out
            </button>
          )}
          {/* THE LENS, UNDER SIGN OUT (owner, 2026-09-15: "move the Owner view
              and location view under SIGNOUT in banner so that it doesnt get in
              the way in the menu and icon line"). It used to ride on the nav
              row (2026-08-15); with six marks there too the row had no room.
              The chip still announces whose view is on screen — that is the
              one thing stopping another clinic's data being read as your own. */}
          {/* The dropdown already says whose view is on screen, so the chip
              shows only when there is no dropdown (owner, 2026-09-24: "drop
              down for viewing as and another chip above looks bad"). */}
          {lensName && !canViewAs ? (
            <span class="role-chip" data-testid="role-chip">
              Viewing as {lensName}
            </span>
          ) : null}
          {canViewAs && (
            <select
              aria-label="Viewing as"
              data-testid="view-as-select"
              value={lens ?? ''}
              onChange={(e) => {
                void setViewAs((e.currentTarget as HTMLSelectElement).value || null);
              }}
            >
              <option value="">Owner view (all locations)</option>
              {LOCATION_ORDER.map((code) => (
                <option key={code} value={code}>{LOCATION_NAMES[code]}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      {/* ONE navigation band: the pages, then the marks pushed to the right end
          by `.nav .sep` (owner, 2026-09-15: "move the icons to the right side").
          No Refresh here any more (same ruling) and no picker — see .mast-right. */}
      <div class="navbar">
        <Nav role={role} />
      </div>
    </>
  );
}

/** A small light/dark switch on the date-and-time line (owner, 2026-09-24:
 * "a little toggle next to time"). Starts from the device's setting; a click
 * pins the other one for this browser. */
function ThemeSwitch(): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(currentTheme());
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label="Dark mode"
      title={dark ? 'Dark mode on' : 'Dark mode off'}
      class={dark ? 'theme-switch on' : 'theme-switch'}
      data-testid="theme-toggle"
      onClick={() => { const next: Theme = dark ? 'light' : 'dark'; setTheme(next); setThemeState(next); }}
    >
      {/* A little sun and moon (owner, 2026-09-24) — drawn, not emoji
          (design law: no emoji). The knob slides over the one not in use. */}
      <svg class="theme-icon sun" viewBox="0 0 10 10" aria-hidden="true">
        <circle cx="5" cy="5" r="2.2" />
        <g stroke-width="1" stroke-linecap="round">
          <line x1="5" y1="0.6" x2="5" y2="1.6" /><line x1="5" y1="8.4" x2="5" y2="9.4" />
          <line x1="0.6" y1="5" x2="1.6" y2="5" /><line x1="8.4" y1="5" x2="9.4" y2="5" />
          <line x1="1.9" y1="1.9" x2="2.6" y2="2.6" /><line x1="7.4" y1="7.4" x2="8.1" y2="8.1" />
          <line x1="1.9" y1="8.1" x2="2.6" y2="7.4" /><line x1="7.4" y1="2.6" x2="8.1" y2="1.9" />
        </g>
      </svg>
      <svg class="theme-icon moon" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M6.6 1.2a3.9 3.9 0 1 0 2.2 6.6A3.2 3.2 0 0 1 6.6 1.2z" />
      </svg>
      <span class="theme-switch-knob" />
    </button>
  );
}
